import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= Container Types =============
/**
 * Container group types for organizing containers.
 */
export const ContainerGroupType = z.enum([
    'frontend',
    'backend',
    'data',
    'infrastructure',
    'external',
]);
/**
 * Container node types specific to container architecture.
 */
export const ContainerNodeType = z.enum([
    // Frontend containers
    'webApp',
    'mobileApp',
    'spa',
    'ssr',
    // Backend containers
    'apiServer',
    'worker',
    'scheduler',
    'gateway',
    // Data containers
    'database',
    'cache',
    'queue',
    'search',
    // Infrastructure
    'loadBalancer',
    'cdn',
    'storage',
    // External
    'externalApi',
    'thirdParty',
]);
/**
 * Communication protocol types.
 */
export const ProtocolType = z.enum([
    'http',
    'https',
    'grpc',
    'websocket',
    'amqp',
    'tcp',
    'sql',
    'redis',
]);
/**
 * Database technology types.
 */
export const DatabaseType = z.enum([
    'postgresql',
    'mysql',
    'mongodb',
    'sqlite',
    'redis',
    'elasticsearch',
    'dynamodb',
]);
/**
 * Queue technology types.
 */
export const QueueType = z.enum([
    'rabbitmq',
    'redis',
    'sqs',
    'kafka',
    'bullmq',
]);
/**
 * Frontend framework types.
 */
export const FrontendFramework = z.enum([
    'react',
    'vue',
    'angular',
    'nextjs',
    'nuxt',
    'svelte',
    'remix',
]);
/**
 * Backend framework types.
 */
export const BackendFramework = z.enum([
    'express',
    'fastify',
    'nestjs',
    'koa',
    'hapi',
    'django',
    'flask',
    'fastapi',
    'spring',
]);
// ============= Container Group =============
/**
 * A group of containers (e.g., Frontend, Backend, Data).
 */
export const ContainerGroupSchema = z.object({
    id: z.string(),
    type: ContainerGroupType,
    label: z.string(),
    description: z.string().optional(),
    containerIds: z.array(z.string()),
});
// ============= Container Node =============
/**
 * Extended node for container architecture.
 */
export const ContainerNodeSchema = z.object({
    id: z.string(),
    containerType: ContainerNodeType,
    label: z.string(),
    description: z.string().optional(),
    technology: z.string().optional(),
    framework: z.string().optional(),
    port: z.number().optional(),
    replicas: z.number().optional(),
    groupId: z.string().optional(),
    // Position for React Flow
    position: z.object({
        x: z.number(),
        y: z.number(),
    }),
    // Docker-specific metadata
    dockerImage: z.string().optional(),
    dockerService: z.string().optional(),
    environment: z.record(z.string()).optional(),
    /** Code evidence supporting this container node (Issue #203) */
    evidence: z.array(CodeEvidenceSchema).optional(),
});
// ============= Container Connection =============
/**
 * Connection between containers.
 */
export const ContainerConnectionSchema = z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    protocol: ProtocolType.optional(),
    label: z.string().optional(),
    async: z.boolean().default(false),
    bidirectional: z.boolean().default(false),
});
// ============= Container Diagram Data =============
/**
 * Complete container architecture diagram data.
 */
export const ContainerDiagramDataSchema = z.object({
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        version: z.string().optional(),
        sourceFiles: z.array(z.string()).optional(),
        generatedAt: z.date().optional(),
    }),
    groups: z.array(ContainerGroupSchema),
    containers: z.array(ContainerNodeSchema),
    connections: z.array(ContainerConnectionSchema),
});
// ============= Detection Result Types =============
/**
 * Docker service detected from docker-compose.
 */
export const DockerServiceSchema = z.object({
    name: z.string(),
    image: z.string().optional(),
    build: z.string().optional(),
    ports: z.array(z.string()).optional(),
    depends_on: z.array(z.string()).optional(),
    environment: z.record(z.string()).optional(),
    volumes: z.array(z.string()).optional(),
});
/**
 * Detection result from analyzing a repository.
 * Issue #178: Added evidence fields for regex-based discovery.
 */
export const ContainerDetectionResultSchema = z.object({
    dockerServices: z.array(DockerServiceSchema),
    databases: z.array(z.object({
        type: DatabaseType,
        name: z.string(),
        source: z.string(),
        /** Code evidence showing where this database was detected (Issue #178) */
        evidence: z.array(CodeEvidenceSchema).optional(),
    })),
    queues: z.array(z.object({
        type: QueueType,
        name: z.string(),
        source: z.string(),
        /** Code evidence showing where this queue was detected (Issue #178) */
        evidence: z.array(CodeEvidenceSchema).optional(),
    })),
    caches: z.array(z.object({
        type: z.string(),
        name: z.string(),
        source: z.string(),
        /** Code evidence showing where this cache was detected (Issue #178) */
        evidence: z.array(CodeEvidenceSchema).optional(),
    })),
    frontends: z.array(z.object({
        framework: FrontendFramework,
        name: z.string(),
        source: z.string(),
        /** Code evidence showing where this frontend was detected (Issue #178) */
        evidence: z.array(CodeEvidenceSchema).optional(),
    })),
    backends: z.array(z.object({
        framework: BackendFramework,
        name: z.string(),
        source: z.string(),
        /** Code evidence showing where this backend was detected (Issue #178) */
        evidence: z.array(CodeEvidenceSchema).optional(),
    })),
    externalApis: z.array(z.object({
        name: z.string(),
        url: z.string().optional(),
        source: z.string(),
        /** Code evidence showing where this external API was detected (Issue #178) */
        evidence: z.array(CodeEvidenceSchema).optional(),
    })),
});
// ============= Helper Functions =============
/**
 * Map container type to unified node type.
 */
export function containerTypeToNodeType(containerType) {
    switch (containerType) {
        case 'webApp':
        case 'mobileApp':
        case 'spa':
        case 'ssr':
            return 'frontend';
        case 'apiServer':
        case 'worker':
        case 'scheduler':
            return 'service';
        case 'gateway':
            return 'gateway';
        case 'database':
            return 'database';
        case 'cache':
            return 'cache';
        case 'queue':
            return 'queue';
        case 'search':
            return 'database';
        case 'loadBalancer':
            return 'loadBalancer';
        case 'cdn':
            return 'cdn';
        case 'storage':
            return 'storage';
        case 'externalApi':
        case 'thirdParty':
            return 'external';
        default:
            return 'service';
    }
}
/**
 * Convert ContainerDiagramData to UnifiedDiagramData.
 */
export function containerToUnifiedDiagram(data) {
    const nodes = data.containers.map((container) => ({
        id: container.id,
        type: containerTypeToNodeType(container.containerType),
        label: container.label,
        description: container.description,
        position: container.position,
        technology: container.technology || container.framework,
        parentId: container.groupId,
        data: {
            containerType: container.containerType,
            port: container.port,
            replicas: container.replicas,
            dockerImage: container.dockerImage,
            dockerService: container.dockerService,
        },
    }));
    const edges = data.connections.map((conn) => ({
        id: conn.id,
        source: conn.source,
        target: conn.target,
        label: conn.label || conn.protocol,
        type: mapProtocolToEdgeType(conn.protocol),
        animated: conn.async,
    }));
    return {
        metadata: {
            title: data.metadata.title,
            description: data.metadata.description,
            diagramType: undefined,
            category: 'architecture',
            c4Level: 'container',
            layoutDirection: 'TB',
            layoutAlgorithm: 'dagre',
            sourceFiles: data.metadata.sourceFiles,
            generatedAt: data.metadata.generatedAt,
            generatedBy: 'regex',
        },
        nodes,
        edges,
    };
}
/**
 * Map protocol to edge type.
 */
function mapProtocolToEdgeType(protocol) {
    switch (protocol) {
        case 'http':
        case 'https':
        case 'grpc':
            return 'http';
        case 'amqp':
        case 'redis':
            return 'async';
        case 'sql':
            return 'database';
        default:
            return 'default';
    }
}
/**
 * Get group color for container group type.
 */
export function getGroupColor(groupType) {
    switch (groupType) {
        case 'frontend':
            return '#e0f2fe'; // Light blue
        case 'backend':
            return '#dcfce7'; // Light green
        case 'data':
            return '#fef3c7'; // Light yellow
        case 'infrastructure':
            return '#f3e8ff'; // Light purple
        case 'external':
            return '#fee2e2'; // Light red
        default:
            return '#f1f5f9'; // Light gray
    }
}
//# sourceMappingURL=container.js.map